// PsyPractice — hukuki belgeler / English.
//
// Bölüm sırası, başlık numaraları ve hukuki iddialar TÜM dillerde aynıdır; esasa
// ilişkin bir değişiklik bütün dillerde birlikte yapılır ve shared.ts'teki
// LEGAL_VERSION birlikte artar — aksi hâlde kullanıcının onayladığı sürüm ile
// okuduğu metin ayrışır. Yalnızca çeviri eklemek sürümü ARTIRMAZ.
import { LEGAL_VERSION, MERCHANT, type LegalDoc } from "./shared";

// İşletme türünün ADI dile bağlıdır, resmî bilgiler (ad, adres, vergi) değildir —
// bu yüzden shared.ts'te değil, her dilin kendi dosyasında durur.
const OWNER_TYPE = "Sole proprietorship";

const OWNER = 'PsyPractice (hereinafter the "Platform" or "Data Controller")';

export const DOCS: LegalDoc[] = [
  {
    id: "terms",
    title: "Terms of Use & Disclaimer",
    description:
      "Nature and limits of the service, what it is not a substitute for, user undertakings, and a comprehensive waiver placing all liability on the user.",
    updated: LEGAL_VERSION,
    sections: [
      {
        heading: "1. Parties and Acceptance",
        paras: [
          `These Terms of Use are entered into between ${OWNER} and any natural or legal person who accesses or uses the Platform (the "User"). By creating an account, signing in, or using the Platform in any manner, the User confirms that they have read, understood, and unconditionally accept and undertake these Terms in full. Anyone who does not accept these Terms must not use the Platform.`,
        ],
      },
      {
        heading: "2. Nature and Limits of the Service",
        bullets: [
          "PsyPractice is an AI-assisted patient SIMULATION software that lets psychology students and early-career clinicians carry out and record their own interviewing practice in a safe environment.",
          "PsyPractice is not an education or teaching service; it is a software tool that lets the user carry out and record their own practice. The Platform provides no lessons, curriculum, examinations, or documents, and confers no title, qualification, or authorisation whatsoever.",
          "The simulated “patient” is not a real client; its responses are synthesized by artificial intelligence, may be erroneous, incomplete, inconsistent, or misleading, and carry no claim of clinical accuracy or truth.",
        ],
      },
      {
        heading: "2.1. What the Platform Is Not a Substitute For",
        bullets: [
          "It is NOT real therapy or counselling, and it substitutes for no session conducted with a real client.",
          "It is NOT a substitute for clinical supervision. Feedback reports produced by the supervisor AI are automatically generated text; they are not expert opinion, an official assessment, or a grade.",
          "It is NOT a diagnostic, screening, or assessment instrument; no output of the Platform may be used for diagnostic or clinical decision-making about any real person.",
          "It MUST NOT be used in crises or emergencies. In cases of suicidal ideation, risk of harm to oneself or others, or any comparable emergency, close the Platform and contact your local emergency services or a competent health institution immediately.",
          "It is NOT a source of professional competence, licensure, title, or authority; time spent on the Platform and its outputs may not be presented to any institution as proof of qualification.",
        ],
      },
      {
        heading: "3. User Representations and Undertakings",
        bullets: [
          "The User undertakes to use the Platform solely within lawful and ethical bounds, for the purpose of developing their own professional practice.",
          "The User agrees NOT to enter any REAL patient/client data, identifying information, or special-category health data belonging to any person; all legal consequences of doing so rest exclusively with the User.",
          "The User undertakes not to use any output obtained from the Platform for the diagnosis, treatment, intervention, or clinical decision-making concerning any real person.",
          "The User is solely responsible for their own professional competence, scope of authority, and compliance with applicable professional and ethical regulations.",
          "Account security, password confidentiality, and all activity carried out under the User's account are the User's responsibility.",
          "The account is personal; the User undertakes not to share it with others and not to use the Platform through automated means (bots, scripts, etc.). Use of paid plans is subject to a fair-use limit (currently 60 sessions per calendar month), detailed in the Distance Sales Agreement.",
        ],
      },
      {
        heading: "4. All Liability Rests with the User (Waiver)",
        paras: [
          "The Platform is provided “AS IS” and “AS AVAILABLE”, without any express or implied warranty whatsoever (including uninterrupted operation, error-freeness, fitness for a particular purpose, accuracy, or currency).",
          "The User accepts and undertakes that they are EXCLUSIVELY and PERSONALLY responsible for any and all direct, indirect, incidental, special, or consequential damages (including material, moral, professional-reputation, data-loss, revenue-loss, and damages caused to third parties) arising from their use of, or inability to use, the Platform, from relying on AI outputs, or from applying such outputs in any way.",
          "To the fullest extent permitted by applicable law, the User hereby irrevocably and unconditionally WAIVES — now and in advance — any and all claims, lawsuits, and rights to compensation that they might raise against the Platform, its owners, employees, developers, business partners, and service providers in respect of any harm they may face in the event of any adverse outcome.",
          "The User further undertakes to hold the Platform fully harmless (indemnify) from any and all claims, damages, penalties, and costs (including attorneys' fees) should the Platform be approached by any third party or institution due to the User's own fault, negligence, or use in breach of these Terms.",
        ],
      },
      {
        heading: "5. Limitation of Liability",
        paras: [
          "Without prejudice to the mandatory provisions of applicable law, the Platform's total legal liability shall in any event be limited to the amount actually paid by the User to the Platform in the 12 months preceding the event giving rise to the relevant claim. Where use is free of charge, this amount is zero.",
        ],
      },
      {
        heading: "6. Intellectual Property, Modification, and Termination",
        bullets: [
          "All intellectual property rights in the Platform, its software, design, patient case templates, and content belong to the Platform.",
          "All transferable economic intellectual-property rights in any content the User creates on the Platform (including the therapist messages they write, session transcripts, and materials derived therefrom) are deemed assigned to the Platform exclusively, perpetually, and royalty-free at the moment such content is created; the Platform may use, reproduce, and process this content worldwide and without restriction for any purpose, including operating the service, generating feedback, and improving and developing the system (including machine-learning improvement). The User's personal-data rights under applicable data-protection law (GDPR / KVKK) are unaffected by this assignment and remain reserved.",
          "The Platform reserves the right to modify, suspend, or discontinue these Terms and the scope of the service at any time, without prior notice.",
          "In the event of a breach of these Terms, the Platform may suspend or close the account without prior notice.",
        ],
      },
      {
        heading: "7. Governing Law and Jurisdiction",
        paras: [
          "These Terms are governed by the laws of the Republic of Türkiye. The courts and enforcement offices of the Republic of Türkiye have jurisdiction over any dispute that may arise.",
          "For consumers resident in the European Union, the protections afforded by the mandatory consumer-protection provisions of their country of residence and by their data-protection rights under the GDPR are reserved; this choice of law does not override those mandatory provisions.",
        ],
      },
    ],
  },
  {
    id: "privacy",
    title: "Privacy Policy",
    description:
      "GDPR as the baseline standard; data processed, legal bases, EU hosting, international transfers (AI provider), and the KVKK Art. 9 cross-border transfer clause for users in Türkiye.",
    updated: LEGAL_VERSION,
    sections: [
      {
        paras: [
          `This Privacy Policy explains how personal data is processed within the Platform operated by ${OWNER}. As its baseline data-protection standard, the policy follows the EU General Data Protection Regulation (GDPR / 2016/679); for users located in Türkiye, the Turkish Personal Data Protection Law No. 6698 (KVKK) — in particular Article 9 on transfers abroad — additionally applies. The KVKK Information Notice and the Explicit Consent Statement are integral parts of this policy.`,
        ],
      },
      {
        heading: "Data controller and hosting location",
        bullets: [
          "Data controller: PsyPractice. Data-subject requests are handled through the contact channel announced on the Platform (contact email: info@psycora.com).",
          "Hosting: the Platform and its database are hosted within the European Union — in a data center in the Netherlands (Oracle Cloud). For users located in the EU/EEA, data is processed and stored within the EU.",
          "The hosting provider acts solely as a processor, under an executed Data Processing Agreement (DPA).",
        ],
      },
      {
        heading: "Data collected",
        bullets: [
          "Account data: name, email, hashed password, subscription/plan information.",
          "Usage data: created sessions, session transcripts, generated patient cases, supervisor feedback reports.",
          "Technical data: session records, IP, browser/locale preference, rate-limit and security logs.",
        ],
      },
      {
        heading: "Purposes of processing and legal basis (GDPR Art. 6)",
        bullets: [
          "Performance of a contract (Art. 6(1)(b)): account creation, running sessions, multi-session patient memory, supervisor evaluation, subscription and usage-limit management.",
          "Legitimate interest (Art. 6(1)(f)): security, abuse prevention, system improvement, and debugging.",
          "Legal obligation (Art. 6(1)(c)): retention/reporting arising from legislation.",
          "Consent (Art. 6(1)(a)): analytics/product-improvement processing not strictly necessary for the service and, where required, transfer outside the EU/EEA.",
        ],
      },
      {
        heading: "International transfers",
        paras: [
          "Because hosting is within the EU (Netherlands), the data of EU/EEA users is, as a rule, processed within the EU with no transfer to a third country at that layer. However, to generate AI responses, session content and prompts are transmitted to the AI service provider (Anthropic), whose processing infrastructure may be located outside the EU/EEA (e.g. the United States).",
          "Such transfers outside the EU/EEA rely on appropriate safeguards under GDPR Art. 44 et seq. — in particular the European Commission's Standard Contractual Clauses (SCCs) — and a data processing agreement (DPA) is concluded with the provider. The Platform does NOT sell personal data for marketing purposes; data is not shared with third parties except where strictly necessary to operate the service (lawful requests by competent authorities reserved).",
        ],
      },
      {
        heading: "For users in Türkiye — KVKK Art. 9 (transfer abroad)",
        paras: [
          "The personal data of users located in Türkiye is transferred outside Türkiye because hosting is in the Netherlands (EU), and may also be transferred to Anthropic for AI processing. These transfers fall under KVKK Art. 9 and rely on appropriate safeguards (the standard contract / undertaking published by the Board) or, where required, on your explicit consent. The scope, recipients, and purpose of the transfer are detailed in the KVKK Information Notice and the Explicit Consent Statement.",
        ],
      },
      {
        heading: "Data-subject rights (GDPR Art. 15–22)",
        bullets: [
          "Access, rectification, erasure ('right to be forgotten'), and restriction of processing.",
          "Data portability: receiving the data you provided in a structured, commonly used, machine-readable format.",
          "Objection: objecting to processing based on legitimate interest and to direct marketing.",
          "Rights relating to solely automated individual decision-making/profiling.",
          "Withdrawing consent at any time for consent-based processing (withdrawal does not affect the lawfulness of processing up to that point).",
          "The right to lodge a complaint with a supervisory authority: the relevant data protection authority in the EU; the Personal Data Protection Authority (KVKK) in Türkiye.",
        ],
      },
      {
        heading: "Retention and security",
        bullets: [
          "Data is retained for as long as the processing purpose requires and for the periods prescribed by applicable law; once the period expires it is deleted or anonymized.",
          "Sensitive fields (e.g. the password, verification and reset codes) are kept encrypted/hashed; access is permission-based.",
          "As no method of transmission or storage is 100% secure, absolute security cannot be guaranteed.",
        ],
      },
      {
        heading: "Prohibition on real patient data",
        paras: [
          "The Platform is a simulation software; entering personal/health data belonging to real clients is prohibited. All liability arising from the User entering data in breach of this prohibition rests exclusively with the User.",
        ],
      },
    ],
  },
  {
    id: "kvkk",
    title: "KVKK Information Notice",
    description:
      "Data controller, processing purposes, legal basis, transfer, and data-subject rights under Article 10 of Law No. 6698.",
    updated: LEGAL_VERSION,
    sections: [
      {
        heading: "Data Controller",
        paras: [
          `Pursuant to Article 10 of the Turkish Personal Data Protection Law No. 6698 (“KVKK”), your personal data is processed by ${OWNER} in its capacity as Data Controller, within the scope described below.`,
        ],
      },
      {
        heading: "Data processed and purposes",
        bullets: [
          "Identity/Contact (name, email): account creation, authentication, communication.",
          "Customer transaction (subscription, plan, usage): service delivery, billing, limit enforcement.",
          "Transaction security (session, IP, logs): security, abuse prevention, legal obligation.",
          "User content (session transcripts, generated cases): delivery of the software service and feedback generation.",
          "Institutional cohort membership (cohort identifier, membership period): where the user joins a cohort run by an institution, visibility to the cohort's instructor team (the instructor and the co-instructor and assistant the instructor adds to the cohort), approval or adjustment of scores, and administration of the cohort.",
        ],
      },
      {
        heading: "Legal basis (Art. 5)",
        bullets: [
          "Conclusion/performance of a contract: data necessary to provide the service.",
          "Legitimate interest of the data controller: security, fraud prevention, system improvement.",
          "Legal obligation: retention/reporting arising from legislation.",
          "Explicit consent: transfer abroad (Art. 9) and additional processing not strictly necessary for the service.",
        ],
      },
      {
        heading: "Transfer abroad (Art. 9)",
        paras: [
          "Because the Platform and its database are hosted within the European Union — in a data center in the Netherlands (Oracle Cloud) — the personal data of users located in Türkiye is transferred outside Türkiye (to the EU). In addition, to generate AI responses, session content and prompts may be transferred to the AI service provider (Anthropic), whose processing infrastructure may be located outside the EU/EEA (e.g. the United States).",
          "These transfers abroad fall under KVKK Art. 9 and rely on appropriate safeguards — such as the standard contract / undertaking published by the Board — or, where required, on your explicit consent. Data processing agreements (DPAs) are concluded with the hosting and service providers.",
        ],
      },
      {
        heading: "Institutional cohorts and instructor visibility",
        paras: [
          "If you join a cohort opened by a university, clinic, or similar institution, your session content (transcripts) and supervisor reports can be seen by that cohort's instructor team (the cohort's instructor and the co-instructor and assistant the instructor adds to the cohort) for the duration of the cohort. The instructor team can approve or adjust the scores in your supervisor reports; it cannot change your session content or your reports. This visibility covers only your sessions attached to the cohort and ends when your cohort membership ends.",
          "This access is obtained through a SEPARATE explicit-consent screen shown when you join the cohort; it is not part of the Platform's general acceptance. If you do not give that consent you cannot join the cohort, but you can carry on using the Platform individually exactly as before. Withdrawing the consent ends your cohort membership and leaves your individual use unaffected.",
        ],
      },
      {
        heading: "Data-subject rights (Art. 11)",
        bullets: [
          "To learn whether your personal data is processed and, if so, to request information.",
          "To learn the purpose of processing and whether it is used in accordance with that purpose.",
          "To know the third parties to whom data is transferred domestically or abroad.",
          "To request rectification of incomplete/incorrect processing and, where conditions are met, deletion/destruction.",
          "To object to a result against you arising solely from automated analysis.",
          "To claim compensation for damage suffered due to unlawful processing.",
        ],
      },
      {
        heading: "Applications",
        paras: [
          "You may submit requests concerning your rights through the contact channel announced by the Data Controller. Applications are concluded within the periods prescribed by the KVKK and its secondary legislation.",
        ],
      },
    ],
  },
  {
    id: "consent",
    title: "Explicit Consent Statement",
    description:
      "Separate from the information notice; consent freely given to transfer abroad and AI processing.",
    updated: LEGAL_VERSION,
    sections: [
      {
        paras: [
          "I declare that I have read and understood the KVKK Information Notice, and that I freely, in an informed and explicit manner, consent to the processing described below. (Pursuant to KVKK Board decision No. 2018/90, explicit consent is obtained separately from the duty to inform.)",
        ],
      },
      {
        heading: "Processing consented to",
        bullets: [
          "Transfer of my personal data outside Türkiye, and its storage and processing there, because the Platform is hosted in the European Union (Netherlands / Oracle Cloud) (KVKK Art. 9).",
          "Transfer and processing of my session transcripts and prompts at the AI service provider (Anthropic), whose processing infrastructure may be located outside the EU/EEA, for the purpose of generating AI responses (KVKK Art. 9).",
          "Processing for product improvement and analytics that is not strictly necessary to provide the service.",
          "Receiving service-related notifications.",
        ],
      },
      {
        heading: "Withdrawal of consent",
        paras: [
          "I understand that I may withdraw my explicit consent at any time; however, since transfer abroad to the AI provider is technically required for the service to function, I accept that withdrawing this consent may result in my being unable to use the relevant features or the service as a whole. Withdrawal does not affect the lawfulness of processing carried out up to the date of withdrawal.",
        ],
      },
    ],
  },
  {
    // Counterpart of the TR "outreach" notice — see the comment there.
    id: "outreach",
    title: "Outreach Communication Notice",
    description:
      "For academics and institutional contacts who received a corporate outreach email: where the data came from, why it is processed, the legal basis, and how to opt out.",
    updated: "2026-08-06",
    sections: [
      {
        paras: [
          "This notice informs recipients of corporate outreach emails sent by PsyPractice to university psychology departments and their academic staff, in accordance with Article 10 of the Turkish Personal Data Protection Law No. 6698 (KVKK) and the Communiqué on the Procedures and Principles for Fulfilling the Disclosure Obligation. Because your personal data was not obtained directly from you, the disclosure is made at the latest at the time of first contact, which is why the outreach email links here.",
        ],
      },
      {
        heading: "1. Data controller",
        bullets: [
          `Name: ${MERCHANT.legalName} (${OWNER_TYPE})`,
          `Address: ${MERCHANT.addressEn}`,
          `Tax office / no: ${MERCHANT.taxOffice} / ${MERCHANT.taxNo}`,
          `Email: ${MERCHANT.email}`,
          `Web: ${MERCHANT.website}`,
        ],
      },
      {
        heading: "2. Data processed and its source",
        paras: [
          "Processing is limited to professional contact data: name, academic title, university and department, institutional email address and, where published, institutional phone number.",
          "This data was not collected from you but compiled from the publicly published official department and faculty pages of universities. No special categories of data are processed, and private (non-institutional) email addresses are not used for outreach.",
        ],
      },
      {
        heading: "3. Purpose",
        paras: [
          "Your data is processed to introduce a software tool built for psychology education, to invite your department to a possible pilot or cooperation conversation, and to carry out the correspondence relating to it. It is not used for any other purpose, not sold, and not transferred to third parties for marketing.",
        ],
      },
      {
        heading: "4. Legal basis",
        paras: [
          "Processing relies on the legitimate interests of the data controller under Article 5/2-(f) KVKK, provided this does not harm your fundamental rights and freedoms: contact is made only to an institutional address, only on a matter directly related to the recipient's professional field, and a limited number of times.",
          "The mere fact that data is publicly available online is not by itself a legal basis. The Personal Data Protection Board has held that data being public does not make it usable for every purpose, and that the intent and purpose of making it public must be assessed separately; Article 5/2-(d) is therefore not relied upon.",
        ],
      },
      {
        heading: "5. Transfers and retention",
        paras: [
          "Correspondence is carried out through an email provider whose infrastructure may be located abroad; such transfers are made with appropriate safeguards under Article 9 KVKK.",
          "Your data is deleted once the purpose of the contact ends or upon your request. After a deletion request, a strictly limited 'do not contact' record is kept solely to make sure you are not contacted again; it is never used for outreach.",
        ],
      },
      {
        heading: "6. Commercial electronic messages and the right to refuse",
        paras: [
          "Promotional messages fall within the scope of Law No. 6563 on the Regulation of Electronic Commerce. You may refuse to receive them without giving any reason; refusals are processed within three business days at the latest, after which no further promotional message is sent. Replying to the email you received, or writing to " + MERCHANT.email + ", is sufficient.",
        ],
      },
      {
        heading: "7. Your rights",
        paras: [
          "Under Article 11 KVKK you have the right to learn whether your personal data is processed, to request information about it, to learn the purpose of processing and whether it is used accordingly, to know the third parties to whom it is transferred in Turkey or abroad, to request correction of incomplete or inaccurate data, to request erasure or destruction, to request that these actions be notified to third parties, to object to a result against you produced solely by automated analysis, and to claim compensation for damage suffered.",
          `Requests may be sent to ${MERCHANT.email} and are answered within thirty days at the latest. You also retain the right to lodge a complaint with the Personal Data Protection Board.`,
        ],
      },
    ],
  },
  {
    id: "cookies",
    title: "Cookie Policy",
    description: "Types of cookies used, their purposes, and preference management.",
    // Kendi tarihi var: yalnızca landing ziyaretçisini ilgilendiren değişiklik
    // LEGAL_VERSION'ı artırıp herkesi yeniden onaya sokmasın (2026-10-28).
    updated: "2026-10-28",
    sections: [
      {
        paras: [
          "The Platform uses cookies and similar local-storage technologies (localStorage) to maintain your session and remember your preferences. The marketing website (psypractice.psycora.com) additionally uses analytics cookies, but only if you accept them.",
        ],
      },
      {
        heading: "Technologies used",
        bullets: [
          "Strictly necessary: session/authentication token, security. The service does not work without these.",
          "Functional: language/locale preference (NEXT_LOCALE), interface preferences (e.g. theme), and your cookie choice on the marketing website (pp_cookie_consent, kept for 12 months).",
          "Performance/security: technical logs for rate-limiting and abuse detection.",
          "Analytics (marketing website only, only with your consent): Google Analytics 4, loaded through Google Tag Manager, measures how visitors find and use the site (cookies _ga and _ga_*, kept for up to 2 years). The application at app.psypractice.psycora.com uses no analytics or advertising cookies.",
          "Advertising: no advertising tags are active at present. If advertising-measurement tags (such as Google Ads) are added, they will run only under the same consent and will be listed here.",
        ],
      },
      {
        heading: "Google and transfer abroad",
        paras: [
          "Analytics data (pseudonymous identifiers, pages visited, device and browser details, approximate location derived from your IP address) is processed by Google Ireland Limited and Google LLC, whose infrastructure may be located in the United States. Google LLC is certified under the EU-U.S. Data Privacy Framework. For users in Türkiye this transfer abroad (KVKK Art. 9) relies on the appropriate safeguards in Google's data processing terms or, where required, on the consent you give in the cookie banner.",
        ],
      },
      {
        heading: "Preference management",
        paras: [
          "You can delete or block cookies in your browser; however, blocking strictly necessary cookies will prevent sign-in and core functionality. In line with GDPR / ePrivacy principles and the KVKK Cookie Guide, non-essential cookies are used only on the basis of your explicit preference/consent.",
          "On the marketing website you can change or withdraw your choice at any time through \"Cookie settings\" in the footer. If you decline, no analytics cookies are set; Google's tag may still send cookieless, aggregated signals (Google Consent Mode) that do not identify your device.",
        ],
      },
    ],
  },
  {
    id: "about",
    title: "About Us",
    description:
      "PsyPractice's purpose, the service it offers, and merchant (seller) contact details.",
    updated: LEGAL_VERSION,
    sections: [
      {
        paras: [
          "PsyPractice is an AI-assisted patient simulation and clinical-skills platform built for psychology students and newly graduated, early-career clinicians. Users conduct text-based therapy sessions; at the end of each session a supervisor AI evaluates the conversation and produces a developmental feedback report. The patient AI carries the therapeutic relationship forward session by session through cross-session memory.",
        ],
      },
      {
        heading: "Our mission",
        paras: [
          "Our aim is to give future mental-health professionals a safe, repeatable environment in which to practice interviewing skills without any risk of harming real clients. PsyPractice is not an education or teaching service; it is a software tool that lets the user carry out and record their own practice. It is not real therapy or counselling, it is not a substitute for clinical supervision, it is not a diagnostic or assessment instrument, and it must not be used in crises or emergencies.",
        ],
      },
      {
        heading: "What we offer",
        bullets: [
          "Text-based therapy session simulation across diverse patient profiles synthesized from clinical literature.",
          "Automated supervisor evaluation reports after each session.",
          "A 'living patient' model with cross-session memory that develops the therapeutic relationship over time.",
          "Custom patient generation, letting students create their own AI cases.",
          "Progress metrics that track microskill development.",
        ],
      },
      {
        heading: "Seller / Business Information",
        bullets: [
          `Trade name: ${MERCHANT.legalName} (${OWNER_TYPE})`,
          `Address: ${MERCHANT.addressEn}`,
          `Tax office / no: ${MERCHANT.taxOffice} / ${MERCHANT.taxNo}`,
          `Email: ${MERCHANT.email}`,
          `Web: ${MERCHANT.website}`,
        ],
      },
    ],
  },
  {
    id: "distance-sales",
    title: "Distance Sales Agreement",
    description:
      "Seller, buyer, service, price, payment, performance, and right-of-withdrawal terms under Turkish Law No. 6502 and the Distance Contracts Regulation.",
    updated: LEGAL_VERSION,
    sections: [
      {
        heading: "1. Parties",
        bullets: [
          `SELLER: ${MERCHANT.legalName} (${OWNER_TYPE}). Address: ${MERCHANT.addressEn}. Tax office/no: ${MERCHANT.taxOffice} / ${MERCHANT.taxNo}. Email: ${MERCHANT.email}.`,
          "BUYER: the natural or legal person who creates an account on the Platform and purchases a paid subscription (the \"Buyer\").",
        ],
      },
      {
        heading: "2. Subject of the Agreement",
        paras: [
          "The subject of this Agreement is to determine the rights and obligations of the parties, in accordance with Turkish Law No. 6502 on Consumer Protection and the Distance Contracts Regulation, regarding the sale and performance of the digital service subscription purchased electronically by the Buyer through the Platform, the characteristics and sale price of which are stated below.",
        ],
      },
      {
        heading: "3. Nature of the Service and Price",
        bullets: [
          "The service sold is the AI-assisted patient-simulation features offered under a paid subscription plan of the PsyPractice software (a digital service / right to use the software).",
          "PsyPractice is not an education or teaching service; it is a software tool that lets the user carry out and record their own practice. The subscription fee is consideration for access to that software; it is not a fee for lessons, curriculum, examinations, consultancy, or any document.",
          "The plan content, term (monthly, a 4-month term, or annual) and current price are shown clearly on the Platform's pricing/checkout screen at the moment of purchase; the total amount including taxes is disclosed to the Buyer before payment.",
          "Prices apply in the currency displayed on the Platform; unless cancelled, the subscription renews automatically at the same price at the end of the selected term.",
          "On plans offered as “unlimited sessions”, use is subject to a fair-use limit (currently 60 sessions per calendar month). Once the limit is reached, new sessions can be started again from the first day of the following calendar month; this gives rise to no price reduction or refund. The limit exists to prevent account sharing and automated use, and the Seller will not change it to the Buyer's detriment during a paid subscription term.",
        ],
      },
      {
        heading: "4. Payment",
        paras: [
          "Payments are collected securely by credit/debit card through the licensed payment institution iyzico. Card details are not stored by the Seller; the payment is processed through iyzico's secure systems. For recurring (subscription) payments, the amount is automatically charged each renewal period to the card approved by the Buyer.",
        ],
      },
      {
        heading: "5. Performance and Delivery",
        paras: [
          "The service is digital and involves no physical delivery. Upon confirmation of payment, the subscription features are made available in the Buyer's account immediately. Performance takes place electronically by providing uninterrupted access throughout the subscription term.",
        ],
      },
      {
        heading: "6. Right of Withdrawal and Its Exception",
        bullets: [
          "Pursuant to Article 15/1-(ğ) of the Distance Contracts Regulation, the right of withdrawal cannot be exercised for intangible (digital) goods/services performed instantly in electronic form where performance has begun with the Buyer's consent.",
          "The Buyer acknowledges at the time of purchase that the service will be performed immediately and that, therefore, no right of withdrawal exists. The Buyer may cancel the subscription at any time to stop renewal; cancellation takes effect at the end of the current paid period.",
          "For detailed refund terms, see \"Delivery & Refund Terms\".",
        ],
      },
      {
        heading: "7. Disputes and Entry into Force",
        paras: [
          "In case of a dispute, the Buyer may apply to the Consumer Arbitration Committee or the Consumer Courts at their place of residence within the monetary limits announced by the Ministry of Trade. This Agreement is concluded and enters into force upon the Buyer's approval in electronic form.",
        ],
      },
    ],
  },
  {
    id: "delivery-refund",
    title: "Delivery & Refund Terms",
    description:
      "Delivery of the digital service, subscription cancellation, the withdrawal exception, and refund conditions.",
    updated: LEGAL_VERSION,
    sections: [
      {
        heading: "Delivery (Digital Service)",
        bullets: [
          "PsyPractice is an entirely digital service; there is no physical product shipment or courier delivery.",
          "The moment payment is confirmed, subscription features are activated automatically and instantly in the Buyer's account; there is no separate delivery time or delivery fee.",
          "The service is accessed via the web browser of any internet-connected device.",
        ],
      },
      {
        heading: "Subscription Cancellation",
        bullets: [
          "The Buyer may cancel the subscription at any time from account settings. Cancellation takes effect at the end of the current paid period and stops the next automatic renewal.",
          "After cancellation, access to the service continues for the remainder of the paid period.",
        ],
      },
      {
        heading: "Right of Withdrawal and Refund",
        bullets: [
          "Because the service is a digital service performed immediately with the Buyer's consent, as a rule no right of withdrawal — and thus no refund right — arises under Article 15/1-(ğ) of the Distance Contracts Regulation.",
          "In cases of clear defect such as an erroneous/duplicate charge, complete inability to access the service for technical reasons, or similar, the Buyer may request a refund by contacting the Seller within a reasonable time from the payment date.",
          `Refund requests are sent to ${MERCHANT.email}; approved refunds are returned to the card used for payment through the iyzico infrastructure, as a rule within 14 days. The refund period may vary depending on the bank's processing time.`,
        ],
      },
      {
        heading: "Contact",
        paras: [
          `For all delivery, cancellation and refund matters: ${MERCHANT.email}${MERCHANT.phone ? " · " + MERCHANT.phone : ""}.`,
        ],
      },
    ],
  },
];
